import { useMutation, useQuery } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { volumingContent } from "i18n/constants";
import React, { useRef, useEffect } from "react";
import { useParams } from "react-router";
import { getSelectedForBulkEdit } from "shared/utils";
import AutoSave from "./AutoSave";
import FormPage from "./base/FormPage";
import FormikInputField from "./formFields/FormikInputField";
import {
  GET_PRESCRIPTION_TEMPLATE,
  UPDATE_VOLUMING_RULE,
} from "graphql/PrescriptionQueries";

interface AdditionalOARInfoProps {
  disableEditing: boolean;
}
interface SiteGroupRules {
  defaultValue: string;
  field: { name: string };
  isActive: boolean;
  isSelectedForBulkEdit: boolean;
  isShown: boolean;
}

interface ParamTypes {
  templateId: string;
  groupId: string;
}

interface FormikValues {
  [key: string]: {
    text: string;
    isActive: boolean;
  };
}

const mapSiteGroupRulesToFormikValues = (
  siteGroupRules: SiteGroupRules[],
  variables: string[],
  inBulkEdit: boolean,
) => {
  const vars = variables ?? [];
  const baseValues = vars.reduce(
    (
      fieldValues: Record<string, { text: string; isActive: boolean }>,
      variable: string,
    ) => {
      fieldValues[variable] = { text: "", isActive: false };
      return fieldValues;
    },
    {},
  );

  return (siteGroupRules ?? []).reduce(
    (
      fieldValues: Record<string, { text: string; isActive: boolean }>,
      fieldDetails: SiteGroupRules,
    ) => {
      const { field, defaultValue, isShown } = fieldDetails;
      let value: any;
      try {
        value = JSON.parse(defaultValue);
      } catch {
        value = defaultValue ?? "";
      }
      if (vars.includes(field.name)) {
        const variableSelectedForBulkEdit = getSelectedForBulkEdit(
          siteGroupRules ?? [],
          field.name,
        );
        fieldValues[field.name] = {
          text: value,
          isActive: inBulkEdit ? variableSelectedForBulkEdit : isShown,
        };
      }
      return fieldValues;
    },
    baseValues,
  );
};

const AdditionalOARInfo = (props: AdditionalOARInfoProps) => {
  const { disableEditing } = props;
  const { groupId } = useParams<keyof ParamTypes>();
  const inBulkEdit = window.location.pathname.includes("bulk_edit");
  const formikRef = useRef<FormikProps<FormikValues>>(null);
  const { data, loading } = useQuery(GET_PRESCRIPTION_TEMPLATE, {
    fetchPolicy: "cache-first",
    variables: { id: groupId },
  });
  const [updateVolumingRule] = useMutation(UPDATE_VOLUMING_RULE, {
    refetchQueries: [
      { query: GET_PRESCRIPTION_TEMPLATE, variables: { id: groupId } },
    ],
  });

  useEffect(() => {
    if (data?.prescriptionTemplate?.volumingRules) {
      const newFormValues = mapSiteGroupRulesToFormikValues(
        data.prescriptionTemplate.volumingRules,
        ["Additional_OAR_Info"],
        inBulkEdit,
      );
      formikRef.current?.resetForm({ values: newFormValues });
    }
  }, [data]);

  const handleMutation = (
    fieldName: string,
    value: any,
    isActive?: boolean,
  ) => {
    let variables = { groupId, fieldName, defaultValue: value.toString() };
    if (inBulkEdit) {
      variables = Object.assign({ isSelectedForBulkEdit: isActive }, variables);
    } else if (typeof isActive === "boolean") {
      variables = Object.assign({ isShown: isActive, isActive }, variables);
    }
    updateVolumingRule({ variables });
  };

  const volumingFields = volumingContent.fields;
  if (loading || !data) return <div>Loading...</div>;
  // Formik requires values to be intialized when the component is mounted. If the default
  // values are initialised to empty values then upon data loading it resets the form to correct
  // values but will trigger a network update as the default values didn't match values from
  // network.
  const newFormValues = mapSiteGroupRulesToFormikValues(
    data.prescriptionTemplate?.volumingRules,
    ["Additional_OAR_Info"],
    inBulkEdit,
  ) as FormikValues;
  return (
    <Formik
      innerRef={formikRef}
      initialValues={newFormValues}
      onSubmit={(values: FormikValues) => {
        if (!disableEditing)
          handleMutation(
            "Additional_OAR_Info",
            values.Additional_OAR_Info.text,
            values.Additional_OAR_Info.isActive,
          );
      }}
    >
      <Form>
        <FormPage heading="">
          <FormikInputField
            id="additional-oar-info"
            label={volumingFields.Additional_OAR_Info}
            textFieldName="Additional_OAR_Info.text"
            checkboxFieldName="Additional_OAR_Info.isActive"
            checkboxType={inBulkEdit ? "checkbox" : "slider"}
            multiline={true}
            disabled={disableEditing}
          />
          {!disableEditing && <AutoSave />}
        </FormPage>
      </Form>
    </Formik>
  );
};

export default AdditionalOARInfo;
